/**
 * Vario embeds our frontend in an iframe with initial height 0. Without continuously sending
 * `postMessage({ appIdentifier, integrationId, height })` to the parent, the iframe stays
 * collapsed and the user sees a "blank" panel.
 *
 * Mirrors @vario-software/vario-app-framework-frontend/height.js + communication.js:
 *   - send `{ appIdentifier, integrationId, ...payload }` to window.parent
 *   - on every DOM mutation + ResizeObserver, post the new scrollHeight
 *   - installation complete / cancel handlers route through the same message channel
 */
function readUrlParam(name: string): string | undefined {
  return new URL(window.location.href).searchParams.get(name) ?? undefined;
}

export const appIdentifier = readUrlParam('appIdentifier');
export const integrationId = readUrlParam('integrationId');

export function sendVarioMessage(payload: Record<string, unknown>) {
  try {
    window.parent.postMessage({ appIdentifier, integrationId, ...payload }, '*');
  } catch {
    // ignore — top-level open
  }
}

/**
 * VARIO-Ready-Signal (App-Framework-Update, Host-Auswertung ab 2026-08-20):
 * Meldet dem Host `{ appIdentifier, integrationId, ready: true }` = App vollständig
 * geladen und kommunikationsbereit. Erst danach gilt die App als erreichbar
 * (Install/Uninstall werden nur dann ausgeführt), sonst Ladeanimation/Fehler.
 * Nach erfolgreichem Bootstrap EINMAL aufrufen.
 */
export function signalVarioReady() {
  sendVarioMessage({ ready: true });
}

/**
 * Neues Vario-Höhenmodell (2026-06): statt einseitig die Content-Höhe zu posten, fordert die App
 * EINMAL `{ useFullHeight: true, appIdentifier, integrationId }` an. Vario setzt das iframe dann auf
 * die volle verfügbare Viewport-Höhe und rechnet bei Layout-Änderungen (Sidebar, Resize) selbst neu.
 * Die App legt ihr Layout auf 100% Höhe aus und scrollt INTERN (siehe html.otto-fullheight). Kein
 * Höhen-Nachmessen/Springen mehr. Re-Send bei load/resize ist idempotent (Vario ignoriert Dubletten).
 */
export function requestVarioFullHeight(): void {
  const send = () => sendVarioMessage({ useFullHeight: true });
  send();
  window.addEventListener('load', send);
  window.addEventListener('resize', send);
  // Sicherheitsnetz: honoriert die Vario-Instanz useFullHeight (noch) NICHT, bekommt das iframe
  // keine Höhe → es bliebe leer/kollabiert (wir posten ja keine Höhe mehr). Bleibt der iframe nach
  // 2s winzig (innerHeight < 200), fallen wir auf das alte Auto-Grow zurück: otto-fullheight-Marker
  // entfernen (sonst deckelt die 100%-Kette body.scrollHeight) + Inhaltshöhe posten.
  setTimeout(() => {
    if (window.innerHeight < 200) {
      document.documentElement.classList.remove('otto-fullheight');
      initVarioIframeHeight('auto', undefined, 540, 1600);
    }
  }, 2000);
}

let lastReported = -1;

/**
 * Iframe-Hoehe je nach Slot-Kontext:
 *   - "fixed": konstanter Korridor (900px) fuer Dashboard-Slot, wo lange
 *     Inhalte (Storefront-Grid, AG-Grid Listings) sonst Endlos-Scroll im
 *     Vario-Parent ausloesen wuerden. Die App-Shell scrollt intern.
 *   - "auto":  ResizeObserver auf body, postet die aktuelle scrollHeight (mit
 *     small min-cap), damit kurze Listing-Sidebar-Slots ihre tatsaechliche
 *     Hoehe bekommen. Vorgesehen fuer listing-status / listing-category.
 */
const FIXED_HEIGHT = 900;
// Feste Hoehe des listing-status-Slots (Vario-Artikel → Verkaufskanaele). Der
// iframe bleibt konstant so hoch, der gesamte Slot-Inhalt scrollt INTERN in
// .kls-shell. Muss in den sichtbaren Vario-Artikel-Bereich passen — Vario
// reicht uns die verfuegbare Hoehe nicht durch (height.js postet nur einseitig),
// daher ein kalibrierter Festwert. Zu gross → unterer Teil ragt aus dem Panel;
// zu klein → unnoetig viel Scroll. Bei Bedarf hier justieren.
export const LISTING_STATUS_HEIGHT = 620;
// AUTO_MIN/MAX gelten fuer install/uninstall/credentials (kompakter Inhalt, waechst
// mit dem Inhalt). listing-status laeuft seit dem eingebetteten Detail-Pane fix.
const AUTO_MIN_HEIGHT = 540;
const AUTO_MAX_HEIGHT = 900;

function postHeight(height: number) {
  if (height === lastReported) return;
  lastReported = height;
  sendVarioMessage({ height });
}

export type IframeHeightMode = 'fixed' | 'auto';

export function initVarioIframeHeight(
  mode: IframeHeightMode = 'fixed',
  fixedHeight: number = FIXED_HEIGHT,
  // Auto-Mode: Untergrenze fuer die Inhaltshoehe. Default AUTO_MIN_HEIGHT (540, fuer
  // install/uninstall/credentials kalibriert). Kompakte Slots (z.B. der Sales-Channel-
  // Einstellungen-Tab) uebergeben einen kleineren Wert, sonst blaeht der 540er-Min die
  // kleine Karte zu einem ueberdimensionierten Fenster auf.
  autoMinHeight: number = AUTO_MIN_HEIGHT,
  // Auto-Mode: Obergrenze. Default AUTO_MAX_HEIGHT (900) schuetzt install/uninstall vor
  // endlos wachsenden Inhalten. Der Settings-Slot uebergibt Infinity → reines Framework-
  // Auto-Grow (height.js-Verhalten): iframe = scrollHeight, Vario scrollt aussen.
  autoMaxHeight: number = AUTO_MAX_HEIGHT,
) {
  if (mode === 'fixed') {
    postHeight(fixedHeight);
    window.addEventListener('resize', () => postHeight(fixedHeight));
    return;
  }

  // Auto-Mode: aktuelle Inhaltshoehe (Body-scrollHeight) tracken und posten.
  // Wir clampen auf [autoMinHeight, autoMaxHeight], damit weder kollabierte Anfangs-
  // Renders (Hoehe 0) noch (bei begrenztem Max) endlos-wachsende Inhalte das Vario-
  // iframe sprengen. Settings uebergibt Max=Infinity → kein oberer Clamp. Updates sind
  // throttled per requestAnimationFrame — mehrere Mutations im selben Frame → ein postMessage.
  let rafScheduled = false;
  const measureAndPost = () => {
    rafScheduled = false;
    const raw = document.body?.scrollHeight ?? autoMinHeight;
    const clamped = Math.max(autoMinHeight, Math.min(autoMaxHeight, raw));
    postHeight(clamped);
  };
  const schedule = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(measureAndPost);
  };
  schedule();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(schedule).observe(document.body);
  }
  // Backup-Listener fuer dyn. Content-Inserts ohne layout-Shift, die der
  // ResizeObserver allein nicht zuverlaessig sieht.
  const mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true, attributes: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
}
